/**
 * Bridges useCollaboration's per-file Yjs Awareness instances to
 * CollaborationPanel, which shows a project-level "who's here" list.
 *
 * vfs-service's collaboration doc is scoped per (projectId, relPath) — there
 * is no project-wide presence channel in vfs-service itself (unlike the old
 * MongoDB sync manager's `/ws/sync?projectId=...`, which was genuinely
 * project-wide). This registry's "current collaborators" is therefore really
 * "who's editing the file that's currently open" rather than "everyone
 * anywhere in this project" — a real, narrower semantic than the old system,
 * accepted as the tradeoff for a project this size rather than adding a
 * second, bespoke presence channel to vfs-service. Since this editor UI only
 * ever has one file open at a time per tab, in practice this covers the
 * common case (people looking at/editing the same file together); it won't
 * show someone editing a *different* file in the same project.
 */

const registries = new Map(); // projectId -> Set<Awareness>
const listeners = new Map(); // projectId -> Set<callback>

function notify(projectId) {
  const cbs = listeners.get(projectId);
  if (!cbs) {
    return;
  }
  cbs.forEach((cb) => {
    try {
      cb();
    } catch (_error) {
      // Listener errors shouldn't break the registry.
    }
  });
}

export function registerAwareness(projectId, awareness) {
  if (!registries.has(projectId)) {
    registries.set(projectId, new Set());
  }
  registries.get(projectId).add(awareness);
  const onChange = () => notify(projectId);
  awareness.on('change', onChange);
  notify(projectId);
  // Stash the listener on the awareness instance itself so unregister can
  // remove the exact same function reference.
  awareness.__registryChangeHandler = onChange;
}

export function unregisterAwareness(projectId, awareness) {
  const set = registries.get(projectId);
  if (set) {
    set.delete(awareness);
    if (set.size === 0) {
      registries.delete(projectId);
    }
  }
  if (awareness.__registryChangeHandler) {
    awareness.off('change', awareness.__registryChangeHandler);
    delete awareness.__registryChangeHandler;
  }
  notify(projectId);
}

/** All distinct remote users currently visible across every awareness instance registered for this project. */
export function getProjectCollaborators(projectId) {
  const set = registries.get(projectId);
  if (!set || set.size === 0) {
    return [];
  }
  const byUserId = new Map();
  set.forEach((awareness) => {
    awareness.getStates().forEach((state, clientId) => {
      if (clientId === awareness.clientID || !state?.user) {
        return;
      }
      byUserId.set(state.user.id, {
        userId: state.user.id,
        username: state.user.name,
        color: state.user.color,
      });
    });
  });
  return Array.from(byUserId.values());
}

export function isProjectConnected(projectId) {
  return registries.has(projectId) && registries.get(projectId).size > 0;
}

/** Subscribe to changes for a project's registered awareness instances. Returns an unsubscribe function. */
export function subscribeProjectAwareness(projectId, callback) {
  if (!listeners.has(projectId)) {
    listeners.set(projectId, new Set());
  }
  listeners.get(projectId).add(callback);
  return () => {
    const cbs = listeners.get(projectId);
    if (cbs) {
      cbs.delete(callback);
      if (cbs.size === 0) {
        listeners.delete(projectId);
      }
    }
  };
}
